import React, { FC, ChangeEvent, useState, useEffect } from 'react';
import { Box, FormControl, Grid, InputLabel, MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material';
import { ClientDirectoryFilter, CustomerType } from 'models/client-directory';
import { useTranslation } from 'react-i18next';
import FilterOnStatusDropdown from 'components/users/shared/filter-on-status-dropdown/filter-on-status-dropdown.component';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { TransportPartner } from 'models/organization';
import CountryInput from 'components/shared/country-input/country-input.component';
import { Country } from 'models/country';
import { useQuery } from '@apollo/client';
import { getMaterialPriceListsTemplate } from 'apollo/templates/material';
import { getActionPriceListsTemplate } from 'apollo/templates/action';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { MaterialPriceList } from 'models/material';
import { ActionPriceList } from 'models/action';
import { countryFromCode } from 'utils/country-helper';
import { CONSTANTS } from 'config/constants';

type ClientDirectoryPageActionsProps = {
    onFilterChange: (clientDirectoryFilter: ClientDirectoryFilter) => void;
    clientListFilter: ClientDirectoryFilter;
};
const ClientDirectoryPageActions: FC<ClientDirectoryPageActionsProps> = ({
    onFilterChange,
    clientListFilter,
}: ClientDirectoryPageActionsProps) => {
    const { t: translate } = useTranslation(['common', 'client-directory', 'organization', 'transport-providers']);
    const [clientDirectoryFilter, setClientDirectoryFilter] = useState<ClientDirectoryFilter>({
        freeText: '',
        type: CustomerType.ALL,
        active: true,
        transportProvider: 'ALL',
        country: '',
        materialPriceList: 'ALL_MATERIAL_PRICE_LISTS',
        actionPriceList: 'ALL_ACTION_PRICE_LISTS',
    });
    const currentOrganization = currentOrgVar();
    const transportProviders = currentOrganization.transportSettings;
    const { showBoundary } = useDenthubErrorBoundary();

    const { data: { materialPriceLists } = {}, loading: materialPriceListsLoading } = useQuery(
        getMaterialPriceListsTemplate,
        {
            onError(error) {
                showBoundary(error);
            },
        },
    );

    const { data: { actionPriceLists } = {}, loading: actionPriceListsLoading } = useQuery(
        getActionPriceListsTemplate,
        {
            onError(error) {
                showBoundary(error);
            },
        },
    );

    useEffect(() => {
        setClientDirectoryFilter(clientListFilter);
    }, [clientListFilter]);

    const updateFilter = (updates: Partial<ClientDirectoryFilter>) => {
        const newFilter = { ...clientDirectoryFilter, ...updates };
        setClientDirectoryFilter(newFilter);
        onFilterChange(newFilter);
    };

    const handleFilterChange = (event: ChangeEvent<HTMLInputElement>) => {
        updateFilter({ freeText: event.target.value });
    };

    const handleTypeOfConnectionChange = (event: SelectChangeEvent) => {
        updateFilter({ type: event.target.value as CustomerType });
    };

    const handleStatusSelected = (active: boolean | null) => {
        updateFilter({ active });
    };

    const handleChangeTransportProvider = (newProvider: string | number) => {
        if (typeof newProvider === 'string') {
            updateFilter({ transportProvider: newProvider });
        }
    };

    const handleCountrySelect = (country: Country) => {
        updateFilter({ country: country.alpha2Code });
    };

    const handlePriceListChange = (event: SelectChangeEvent) => {
        updateFilter({ materialPriceList: event.target.value });
    };
    const handleActionPriceListChange = (event: SelectChangeEvent) => {
        updateFilter({ actionPriceList: event.target.value });
    };

    return (
        <Box
            sx={{
                marginTop: 1,
                marginBottom: 2,
                bgcolor: 'background.paper',
                padding: 2,
                border: '1px solid',
                borderColor: 'divider',
            }}
        >
            <Grid container spacing={1}>
                <Grid item md={6} xs={12}>
                    <TextField
                        id="filter-name"
                        fullWidth
                        onChange={handleFilterChange}
                        value={clientDirectoryFilter.freeText || ''}
                        label={translate('client-directory:SEARCH_CUSTOMER')}
                        placeholder={translate('client-directory:SEARCH_CUSTOMER_PLACEHOLDER')}
                        margin="dense"
                    />
                </Grid>
                <Grid item md={3} xs={12}>
                    <FormControl fullWidth margin="dense">
                        <InputLabel>{translate('client-directory:CUSTOMER_TYPE')}</InputLabel>
                        <Select
                            id="filter-customer-type"
                            defaultValue={'ALL'}
                            label={translate('client-directory:CUSTOMER_TYPE')}
                            onChange={handleTypeOfConnectionChange}
                            value={clientDirectoryFilter.type || CustomerType.ALL}
                        >
                            {Object.values(CustomerType).map((option) => {
                                return (
                                    <MenuItem key={option} value={option}>
                                        {option !== CustomerType.SUPPLIER
                                            ? translate(`client-directory:${option}`)
                                            : translate('client-directory:LAB_CUSTOMER')}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item md={3} xs={12}>
                    <FilterOnStatusDropdown
                        statusSelected={handleStatusSelected}
                        statusValue={clientDirectoryFilter.active}
                        fullWidth
                    />
                </Grid>
                <Grid item md={3} xs={12}>
                    <FormControl fullWidth>
                        <InputLabel id="transport-provider-label">
                            {translate('client-directory:TRANSPORT_PROVIDER')}
                        </InputLabel>
                        <Select
                            labelId="transport-provider-label"
                            value={clientDirectoryFilter.transportProvider || 'ALL'}
                            label={translate('client-directory:TRANSPORT_PROVIDER')}
                            onChange={(e) => handleChangeTransportProvider(e.target.value)}
                        >
                            <MenuItem value="ALL">{translate('transport-providers:ALL')}</MenuItem>
                            <MenuItem value="NONE">{translate('transport-providers:NONE')}</MenuItem>
                            {transportProviders?.denthubTransport?.isEnabled ? (
                                <MenuItem
                                    value={`DENTHUB_TRANSPORT_${transportProviders?.denthubTransport?.externalId}`}
                                >
                                    {translate(
                                        `transport-providers:DENTHUB_TRANSPORT_${transportProviders?.denthubTransport?.externalId}`,
                                    )}
                                </MenuItem>
                            ) : undefined}
                            {transportProviders?.nShiftTransport?.isEnabled
                                ? transportProviders?.nShiftTransport?.partners?.map(
                                      (partner: TransportPartner, index: number) => {
                                          return (
                                              <MenuItem key={index} value={`NSHIFT_${partner.id}`}>
                                                  {translate(`transport-providers:NSHIFT_${partner.id}`)}
                                              </MenuItem>
                                          );
                                      },
                                  )
                                : undefined}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item sm={3} xs={12}>
                    <FormControl fullWidth>
                        <InputLabel>{translate('client-directory:ACTION_PRICE_LISTS')}</InputLabel>
                        <Select
                            value={clientDirectoryFilter.actionPriceList || 'ALL_ACTION_PRICE_LISTS'}
                            label={translate('client-directory:ALL_ACTION_PRICE_LISTS')}
                            onChange={handleActionPriceListChange}
                        >
                            <MenuItem value="ALL_ACTION_PRICE_LISTS">
                                {translate('client-directory:ALL_ACTION_PRICE_LISTS')}
                            </MenuItem>
                            {!actionPriceListsLoading &&
                                actionPriceLists?.map((actionPriceList: ActionPriceList) => (
                                    <MenuItem key={actionPriceList._id} value={actionPriceList._id}>
                                        {actionPriceList.isStandardPriceList
                                            ? `${actionPriceList.actionPriceListName} (${translate(
                                                  'common:DEFAULT',
                                              ).toLowerCase()})`
                                            : actionPriceList.actionPriceListName}{' '}
                                        - {actionPriceList.currency}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item sm={3} xs={12}>
                    <FormControl fullWidth>
                        <InputLabel>{translate('client-directory:MATERIAL_PRICE_LISTS')}</InputLabel>
                        <Select
                            value={clientDirectoryFilter.materialPriceList || 'ALL_MATERIAL_PRICE_LISTS'}
                            label={translate('client-directory:MATERIAL_PRICE_LISTS')}
                            onChange={handlePriceListChange}
                        >
                            <MenuItem value="ALL_MATERIAL_PRICE_LISTS">
                                {translate('client-directory:ALL_MATERIAL_PRICE_LISTS')}
                            </MenuItem>
                            {!materialPriceListsLoading &&
                                materialPriceLists?.map((materialPriceList: MaterialPriceList) => (
                                    <MenuItem key={materialPriceList._id} value={materialPriceList._id}>
                                        {materialPriceList.isStandardPriceList
                                            ? `${materialPriceList.materialPriceListName} (${translate(
                                                  'common:DEFAULT',
                                              ).toLowerCase()})`
                                            : materialPriceList.materialPriceListName}{' '}
                                        - {materialPriceList.currency}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item md={3} xs={12}>
                    <CountryInput
                        country={countryFromCode(clientDirectoryFilter.country)}
                        onCountrySelect={handleCountrySelect}
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

export default ClientDirectoryPageActions;
